import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Baby, Heart, Sparkles, AlertCircle, Scale } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { usersAPI } from '../lib/api';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_rejuven-health/artifacts/rqq06olv_Rejuven%20Logo%20-%20Website.png';

const bloodTypes = [
  { value: 'A', label: 'A' },
  { value: 'B', label: 'B' },
  { value: 'AB', label: 'AB' },
  { value: 'O', label: 'O' },
  { value: 'unknown', label: "Don't know" },
];

const raceOptions = [
  { value: 'american_indian', label: 'American Indian' },
  { value: 'asian', label: 'Asian' },
  { value: 'black', label: 'Black' },
  { value: 'caucasian', label: 'Caucasian' },
  { value: 'hispanic_latino', label: 'Hispanic/Latino' },
  { value: 'other', label: 'Other' },
];

const journeyStages = [
  { value: 'preparing', label: 'Not trying yet but want to prepare for when I do', icon: Sparkles },
  { value: 'trying', label: 'Want to become pregnant', icon: Heart },
  { value: 'ivf', label: 'Currently undergoing IVF', icon: Baby },
  { value: 'pregnant', label: "I'm pregnant", icon: Baby },
  { value: 'postpartum', label: "I've recently given birth", icon: Heart },
];

const healthConditions = [
  { id: 'struggling_conceive', label: 'Struggling to conceive' },
  { id: 'miscarriage', label: 'One or more miscarriages' },
  { id: 'pcos', label: 'PCOS' },
  { id: 'endometriosis', label: 'Endometriosis' },
  { id: 'preeclampsia_history', label: 'A family history of pre-eclampsia' },
  { id: 'diabetes', label: 'Type 1 or 2 diabetes' },
  { id: 'autoimmune', label: 'An autoimmune disease' },
  { id: 'kidney_disease', label: 'Kidney disease' },
  { id: 'chronic_hypertension', label: 'Chronic hypertension' },
  { id: 'gestational_diabetes', label: 'A history of gestational diabetes' },
];

const dietaryOptions = [
  { id: 'none', label: "I don't have any dietary restrictions" },
  { id: 'vegetarian', label: 'I am vegetarian' },
  { id: 'vegan', label: 'I am vegan' },
  { id: 'peanut_allergy', label: 'I am allergic to peanuts' },
  { id: 'tree_nut_allergy', label: 'I am allergic to tree nuts' },
  { id: 'dairy_allergy', label: 'I am allergic to dairy' },
  { id: 'egg_allergy', label: 'I am allergic to eggs' },
  { id: 'fish_allergy', label: 'I am allergic to fish' },
  { id: 'shellfish_allergy', label: 'I am allergic to shellfish' },
  { id: 'coeliac', label: 'I am coeliac' },
  { id: 'gluten_intolerant', label: 'I am gluten intolerant' },
  { id: 'soy_allergy', label: 'I am allergic to soy' },
  { id: 'sesame_allergy', label: 'I am allergic to sesame' },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [useMetric, setUseMetric] = useState(true);
  
  const [formData, setFormData] = useState({
    age: '',
    weight_kg: '',
    weight_lbs: '',
    height_cm: '',
    height_ft: '',
    height_in: '',
    blood_type: '',
    race: '',
    has_children: null,
    children_count: '',
    journey_stage: '',
    health_conditions: [],
    dietary_restrictions: [],
  });

  const totalSteps = 6;

  // Calculate BMI
  const calculateBMI = () => {
    let weight, height;
    if (useMetric) {
      weight = parseFloat(formData.weight_kg);
      height = parseFloat(formData.height_cm) / 100; // convert cm to m
    } else {
      weight = parseFloat(formData.weight_lbs) * 0.453592; // lbs to kg
      const totalInches = (parseFloat(formData.height_ft) || 0) * 12 + (parseFloat(formData.height_in) || 0);
      height = totalInches * 0.0254; // inches to m
    }
    
    if (weight && height && height > 0) {
      return (weight / (height * height)).toFixed(1);
    }
    return null;
  };

  const getBMICategory = (bmi) => {
    if (!bmi) return null;
    const bmiNum = parseFloat(bmi);
    if (bmiNum < 18.5) return { label: 'Underweight', color: 'text-blue-600' };
    if (bmiNum < 25) return { label: 'Normal', color: 'text-green-600' };
    if (bmiNum < 30) return { label: 'Overweight', color: 'text-yellow-600' };
    return { label: 'Obese', color: 'text-red-600' };
  };

  const toggleHealthCondition = (conditionId) => {
    setFormData(prev => ({
      ...prev,
      health_conditions: prev.health_conditions.includes(conditionId)
        ? prev.health_conditions.filter(c => c !== conditionId)
        : [...prev.health_conditions, conditionId]
    }));
  };

  const toggleDietaryRestriction = (restrictionId) => {
    if (restrictionId === 'none') {
      setFormData(prev => ({
        ...prev,
        dietary_restrictions: prev.dietary_restrictions.includes('none') ? [] : ['none']
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        dietary_restrictions: prev.dietary_restrictions.filter(r => r !== 'none').includes(restrictionId)
          ? prev.dietary_restrictions.filter(r => r !== restrictionId)
          : [...prev.dietary_restrictions.filter(r => r !== 'none'), restrictionId]
      }));
    }
  };

  const handleNext = () => {
    if (step === 1) {
      if (!formData.age) {
        toast.error('Please enter your age');
        return;
      }
      const hasWeight = useMetric ? formData.weight_kg : formData.weight_lbs;
      const hasHeight = useMetric ? formData.height_cm : (formData.height_ft || formData.height_in);
      if (!hasWeight || !hasHeight) {
        toast.error('Please enter your weight and height');
        return;
      }
    }
    if (step === 2 && !formData.blood_type) {
      toast.error('Please select your blood type');
      return;
    }
    if (step === 3 && !formData.race) {
      toast.error('Please select your race/ethnicity');
      return;
    }
    if (step === 4 && !formData.journey_stage) {
      toast.error('Please select your pregnancy journey stage');
      return;
    }
    setStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Convert to metric if needed
      let weight_kg = parseFloat(formData.weight_kg);
      let height_cm = parseFloat(formData.height_cm);
      
      if (!useMetric) {
        weight_kg = parseFloat(formData.weight_lbs) * 0.453592;
        const totalInches = (parseFloat(formData.height_ft) || 0) * 12 + (parseFloat(formData.height_in) || 0);
        height_cm = totalInches * 2.54;
      }

      const data = {
        age: parseInt(formData.age),
        weight_kg: Math.round(weight_kg * 10) / 10,
        height_cm: Math.round(height_cm),
        blood_type: formData.blood_type,
        race: formData.race,
        has_children: formData.has_children === true,
        children_count: formData.has_children ? parseInt(formData.children_count) || 0 : 0,
        journey_stage: formData.journey_stage,
        health_conditions: formData.health_conditions,
        dietary_restrictions: formData.dietary_restrictions.filter(r => r !== 'none'),
      };

      const response = await usersAPI.completeOnboarding(data);
      updateUser(response.data);
      toast.success('Welcome to Rejuven!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save profile';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const bmi = calculateBMI();
  const bmiCategory = getBMICategory(bmi);

  const getStepContent = () => {
    // Step 1: Body Measurements
    if (step === 1) {
      return (
        <div className="animate-fade-up">
          <h1 className="font-heading text-3xl lg:text-4xl text-[#1A3C34] mb-3">
            Let&apos;s start with the basics
          </h1>
          <p className="text-[#1A3C34]/70 mb-8">
            This helps us calculate your BMI and personalize your recommendations.
          </p>

          <div className="space-y-6">
            {/* Age */}
            <div className="space-y-2">
              <Label className="text-[#1A3C34] text-lg">How old are you?</Label>
              <Input
                type="number"
                placeholder="Your age"
                value={formData.age}
                onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
                className="input-warm max-w-[200px]"
                min="18"
                max="65"
                data-testid="age-input"
              />
            </div>

            {/* Unit Toggle */}
            <div className="flex items-center gap-4">
              <span className="text-sm text-[#1A3C34]/70">Measurement system:</span>
              <div className="flex rounded-full border border-[#D4A5A5]/30 p-1">
                <button
                  type="button"
                  onClick={() => setUseMetric(true)}
                  className={`px-4 py-1.5 rounded-full text-sm transition-all ${
                    useMetric ? 'bg-[#1A3C34] text-white' : 'text-[#1A3C34]/70'
                  }`}
                  data-testid="metric-toggle"
                >
                  Metric
                </button>
                <button
                  type="button"
                  onClick={() => setUseMetric(false)}
                  className={`px-4 py-1.5 rounded-full text-sm transition-all ${
                    !useMetric ? 'bg-[#1A3C34] text-white' : 'text-[#1A3C34]/70'
                  }`}
                  data-testid="imperial-toggle"
                >
                  Imperial
                </button>
              </div>
            </div>

            {/* Weight & Height */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[#1A3C34]">Weight {useMetric ? '(kg)' : '(lbs)'}</Label>
                {useMetric ? (
                  <Input
                    type="number"
                    placeholder="e.g., 65"
                    value={formData.weight_kg}
                    onChange={(e) => setFormData(prev => ({ ...prev, weight_kg: e.target.value }))}
                    className="input-warm"
                    min="30"
                    max="200"
                    data-testid="weight-input"
                  />
                ) : (
                  <Input
                    type="number"
                    placeholder="e.g., 143"
                    value={formData.weight_lbs}
                    onChange={(e) => setFormData(prev => ({ ...prev, weight_lbs: e.target.value }))}
                    className="input-warm"
                    min="66"
                    max="440"
                    data-testid="weight-input"
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-[#1A3C34]">Height {useMetric ? '(cm)' : ''}</Label>
                {useMetric ? (
                  <Input
                    type="number"
                    placeholder="e.g., 165"
                    value={formData.height_cm}
                    onChange={(e) => setFormData(prev => ({ ...prev, height_cm: e.target.value }))}
                    className="input-warm"
                    min="100"
                    max="220"
                    data-testid="height-input"
                  />
                ) : (
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Input
                        type="number"
                        placeholder="ft"
                        value={formData.height_ft}
                        onChange={(e) => setFormData(prev => ({ ...prev, height_ft: e.target.value }))}
                        className="input-warm"
                        min="3"
                        max="7"
                        data-testid="height-ft-input"
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        type="number"
                        placeholder="in"
                        value={formData.height_in}
                        onChange={(e) => setFormData(prev => ({ ...prev, height_in: e.target.value }))}
                        className="input-warm"
                        min="0"
                        max="11"
                        data-testid="height-in-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* BMI Display */}
            {bmi && (
              <div className="bg-[#F2E8E8] rounded-2xl p-4 flex items-center gap-4 animate-fade-up">
                <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center">
                  <Scale className="w-6 h-6 text-[#1A3C34]" />
                </div>
                <div>
                  <p className="text-sm text-[#1A3C34]/70">Your BMI</p>
                  <p className="text-2xl font-heading text-[#1A3C34]">
                    {bmi} <span className={`text-sm ${bmiCategory?.color}`}>({bmiCategory?.label})</span>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Step 2: Blood Type
    if (step === 2) {
      return (
        <div className="animate-fade-up">
          <h1 className="font-heading text-3xl lg:text-4xl text-[#1A3C34] mb-3">
            What is your blood type?
          </h1>
          <p className="text-[#1A3C34]/70 mb-8">
            Blood type can influence fertility and pregnancy recommendations.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {bloodTypes.map((type) => {
              const isSelected = formData.blood_type === type.value;
              return (
                <button
                  key={type.value}
                  onClick={() => setFormData(prev => ({ ...prev, blood_type: type.value }))}
                  className={`p-5 rounded-2xl border-2 text-center transition-all ${
                    isSelected
                      ? 'border-[#D4A5A5] bg-[#D4A5A5]/10'
                      : 'border-[#D4A5A5]/20 bg-white hover:border-[#D4A5A5]/40'
                  }`}
                  data-testid={`blood-type-${type.value}`}
                >
                  <span className={`text-xl font-heading ${isSelected ? 'text-[#1A3C34]' : 'text-[#1A3C34]/70'}`}>
                    {type.label}
                  </span>
                  {isSelected && <Check className="w-5 h-5 text-[#D4A5A5] mx-auto mt-2" />}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    // Step 3: Race/Ethnicity + Children
    if (step === 3) {
      return (
        <div className="animate-fade-up">
          <h1 className="font-heading text-3xl lg:text-4xl text-[#1A3C34] mb-3">
            Tell us about yourself
          </h1>
          <p className="text-[#1A3C34]/70 mb-8">
            Certain health factors vary across different populations.
          </p>

          <div className="space-y-8">
            {/* Race */}
            <div className="space-y-3">
              <Label className="text-[#1A3C34] text-lg">What is your race/ethnicity?</Label>
              <div className="grid sm:grid-cols-2 gap-3">
                {raceOptions.map((option) => {
                  const isSelected = formData.race === option.value;
                  return (
                    <button
                      key={option.value}
                      onClick={() => setFormData(prev => ({ ...prev, race: option.value }))}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        isSelected
                          ? 'border-[#D4A5A5] bg-[#D4A5A5]/10'
                          : 'border-[#D4A5A5]/20 bg-white hover:border-[#D4A5A5]/40'
                      }`}
                      data-testid={`race-${option.value}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-[#1A3C34]">{option.label}</span>
                        {isSelected && <Check className="w-5 h-5 text-[#D4A5A5]" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Has Children */}
            <div className="space-y-3">
              <Label className="text-[#1A3C34] text-lg">Have you ever had children?</Label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setFormData(prev => ({ ...prev, has_children: true }))}
                  className={`p-5 rounded-2xl border-2 transition-all ${
                    formData.has_children === true
                      ? 'border-[#D4A5A5] bg-[#D4A5A5]/10'
                      : 'border-[#D4A5A5]/20 bg-white hover:border-[#D4A5A5]/40'
                  }`}
                  data-testid="has-children-yes"
                >
                  <span className="font-medium text-[#1A3C34]">Yes</span>
                  {formData.has_children === true && <Check className="w-5 h-5 text-[#D4A5A5] mx-auto mt-2" />}
                </button>
                <button
                  onClick={() => setFormData(prev => ({ ...prev, has_children: false, children_count: '' }))}
                  className={`p-5 rounded-2xl border-2 transition-all ${
                    formData.has_children === false
                      ? 'border-[#D4A5A5] bg-[#D4A5A5]/10'
                      : 'border-[#D4A5A5]/20 bg-white hover:border-[#D4A5A5]/40'
                  }`}
                  data-testid="has-children-no"
                >
                  <span className="font-medium text-[#1A3C34]">No</span>
                  {formData.has_children === false && <Check className="w-5 h-5 text-[#D4A5A5] mx-auto mt-2" />}
                </button>
              </div>

              {formData.has_children === true && (
                <div className="mt-4 animate-fade-up">
                  <Label className="text-[#1A3C34]">How many children do you have?</Label>
                  <Input
                    type="number"
                    placeholder="Number of children"
                    value={formData.children_count}
                    onChange={(e) => setFormData(prev => ({ ...prev, children_count: e.target.value }))}
                    className="input-warm mt-2 max-w-[200px]"
                    min="1"
                    max="20"
                    data-testid="children-count-input"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Step 4: Journey Stage
    if (step === 4) {
      return (
        <div className="animate-fade-up">
          <h1 className="font-heading text-3xl lg:text-4xl text-[#1A3C34] mb-3">
            Where are you in your journey?
          </h1>
          <p className="text-[#1A3C34]/70 mb-8">
            This helps us provide stage-appropriate guidance and support.
          </p>

          <div className="space-y-3">
            {journeyStages.map((stage) => {
              const isSelected = formData.journey_stage === stage.value;
              const IconComponent = stage.icon;
              return (
                <button
                  key={stage.value}
                  onClick={() => setFormData(prev => ({ ...prev, journey_stage: stage.value }))}
                  className={`w-full p-5 rounded-2xl border-2 text-left transition-all ${
                    isSelected
                      ? 'border-[#D4A5A5] bg-[#D4A5A5]/10'
                      : 'border-[#D4A5A5]/20 bg-white hover:border-[#D4A5A5]/40'
                  }`}
                  data-testid={`journey-${stage.value}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      isSelected ? 'bg-[#D4A5A5]' : 'bg-[#D4A5A5]/20'
                    }`}>
                      <IconComponent className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-[#D4A5A5]'}`} />
                    </div>
                    <div className="flex-1">
                      <span className="font-medium text-[#1A3C34]">{stage.label}</span>
                    </div>
                    {isSelected && <Check className="w-5 h-5 text-[#D4A5A5]" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    // Step 5: Health Conditions
    if (step === 5) {
      return (
        <div className="animate-fade-up">
          <h1 className="font-heading text-3xl lg:text-4xl text-[#1A3C34] mb-3">
            Health history
          </h1>
          <p className="text-[#1A3C34]/70 mb-2">
            We know this is a very hard subject to open up about, but understanding your health history helps us provide better support.
          </p>
          <p className="text-sm text-[#1A3C34]/50 mb-8">
            Do you now or have you ever experienced any of the following? (Select all that apply)
          </p>

          <div className="grid sm:grid-cols-2 gap-3">
            {healthConditions.map((condition) => {
              const isSelected = formData.health_conditions.includes(condition.id);
              return (
                <button
                  key={condition.id}
                  onClick={() => toggleHealthCondition(condition.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    isSelected
                      ? 'border-[#D4A5A5] bg-[#D4A5A5]/10'
                      : 'border-[#D4A5A5]/20 bg-white hover:border-[#D4A5A5]/40'
                  }`}
                  data-testid={`condition-${condition.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#1A3C34]">{condition.label}</span>
                    {isSelected && <Check className="w-4 h-4 text-[#D4A5A5]" />}
                  </div>
                </button>
              );
            })}
          </div>

          <p className="text-sm text-[#1A3C34]/50 mt-6 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            You can skip this step if none apply. Your information is kept confidential.
          </p>
        </div>
      );
    }

    // Step 6: Dietary Restrictions
    return (
      <div className="animate-fade-up">
        <h1 className="font-heading text-3xl lg:text-4xl text-[#1A3C34] mb-3">
          Dietary preferences
        </h1>
        <p className="text-[#1A3C34]/70 mb-8">
          Which of the following apply to you? This helps us tailor nutrition recommendations.
        </p>

        <div className="grid sm:grid-cols-2 gap-3">
          {dietaryOptions.map((option) => {
            const isSelected = formData.dietary_restrictions.includes(option.id);
            const isDisabled = option.id !== 'none' && formData.dietary_restrictions.includes('none');
            return (
              <button
                key={option.id}
                onClick={() => toggleDietaryRestriction(option.id)}
                disabled={isDisabled}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  isSelected
                    ? 'border-[#D4A5A5] bg-[#D4A5A5]/10'
                    : isDisabled
                    ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                    : 'border-[#D4A5A5]/20 bg-white hover:border-[#D4A5A5]/40'
                }`}
                data-testid={`dietary-${option.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#1A3C34]">{option.label}</span>
                  {isSelected && <Check className="w-4 h-4 text-[#D4A5A5]" />}
                </div>
              </button>
            );
          })}
        </div>

        {/* Summary Card */}
        <div className="bg-[#F2E8E8] rounded-2xl p-6 mt-8">
          <h3 className="font-heading text-lg text-[#1A3C34] mb-3">Your Profile Summary</h3>
          <div className="space-y-2 text-sm text-[#1A3C34]/70">
            <p><strong>Age:</strong> {formData.age} years</p>
            <p><strong>BMI:</strong> {bmi || 'N/A'} {bmiCategory ? `(${bmiCategory.label})` : ''}</p>
            <p><strong>Blood Type:</strong> {bloodTypes.find(b => b.value === formData.blood_type)?.label || 'N/A'}</p>
            <p><strong>Journey Stage:</strong> {journeyStages.find(s => s.value === formData.journey_stage)?.label || 'N/A'}</p>
            {formData.has_children && (
              <p><strong>Children:</strong> {formData.children_count || 0}</p>
            )}
            {formData.health_conditions.length > 0 && (
              <p><strong>Health Considerations:</strong> {formData.health_conditions.length} condition(s) noted</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      {/* Header */}
      <header className="py-6 px-8 border-b border-[#D4A5A5]/10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img src={LOGO_URL} alt="Rejuven" className="h-10 w-auto" />
          <div className="flex items-center gap-3">
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => (
              <div
                key={s}
                className={`w-10 h-1.5 rounded-full transition-all ${
                  s <= step ? 'bg-[#D4A5A5]' : 'bg-[#D4A5A5]/20'
                }`}
              />
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 py-12 px-8">
        <div className="max-w-2xl mx-auto">
          {getStepContent()}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-12">
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={step === 1}
              className="text-[#1A3C34]"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>

            {step < totalSteps ? (
              <Button
                onClick={handleNext}
                className="bg-[#1A3C34] text-white hover:bg-[#152E28] rounded-full px-8"
                data-testid="next-btn"
              >
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-[#D4A5A5] text-[#1A3C34] hover:bg-[#C89494] rounded-full px-8"
                data-testid="complete-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-[#1A3C34]/30 border-t-[#1A3C34] rounded-full animate-spin" />
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Complete Setup <Check className="w-4 h-4 ml-1" />
                  </span>
                )}
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
